/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from "@emotion/core";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import Modal from "@material-ui/core/Modal";
import React from "react";
import LazyLoad from "react-lazyload";

import { MEDIA_QUERY_FOR_SP } from "../../constants/mediaQueryForSp";
import { EventGoodsStatusBadges } from "../eventGoods/EventGoodsStatusBadges";

import type { WatchlistItem } from "./types/WatchlistItem";

// モーダル内専用のカードコンポーネント
const ModalWatchListCard: React.FC<{
  item: WatchlistItem;
}> = ({ item }) => {
  const cardClasses = cardStyles();

  return (
    <Card css={cardClasses.goodsItem} variant="outlined">
      <div css={cardClasses.imageContainer}>
        <LazyLoad height={"100%"} offset={400} once>
          <img
            className="goodsImage"
            src={item.thumbnail}
            alt={item.className}
          />
        </LazyLoad>
      </div>

      <div css={cardClasses.goodsItemInfo}>
        <h3>
          {item.className}
          {item.hasVariants && item.subClassName
            ? `（${item.subClassName}）`
            : ""}
        </h3>
        <p css={cardClasses.goodsItemPrice}>
          {item.hasVariants ? "各 ¥" : "¥"}
          {item.price.toLocaleString()}
          {EventGoodsStatusBadges[item.eventGoodsStatus]}
        </p>
      </div>
    </Card>
  );
};

type Props = {
  selectedItem: WatchlistItem | undefined;
  onClickClose: React.MouseEventHandler<HTMLButtonElement>;
  onClickConfirmRemove: () => void;
  isLoadingConfirmRemoveButton?: boolean;
};

export const AccountWatchlistModal: React.FC<Props> = ({
  selectedItem,
  onClickClose,
  onClickConfirmRemove,
  isLoadingConfirmRemoveButton,
}) => {
  const classes = modalStyles();

  return (
    <Modal
      open={selectedItem !== undefined}
      onClose={onClickClose}
      css={classes.modal}
    >
      <div css={classes.modalContent}>
        <h3 css={classes.modalTitle}>ウォッチリストから削除しますか？</h3>

        {selectedItem && (
          <div css={classes.modalCardWrapper}>
            <ModalWatchListCard item={selectedItem} />
          </div>
        )}

        <div css={classes.modalButtons}>
          <Button
            variant="outlined"
            color="default"
            onClick={onClickClose}
            css={classes.cancelButton}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="default"
            onClick={onClickConfirmRemove}
            css={classes.deleteButton}
            disabled={isLoadingConfirmRemoveButton}
          >
            <span css={classes.loadingWrapper}>
              {isLoadingConfirmRemoveButton && <span css={classes.spinner} />}
              削除する
            </span>
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const modalStyles = () => {
  return {
    modal: css`
      display: flex;
      align-items: center;
      justify-content: center;
    `,
    loadingWrapper: css`
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    `,
    spinner: css`
      width: 18px;
      height: 18px;
      border: 2px solid #fff;
      border-top: 2px solid #666;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
      @keyframes spin {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }
    `,
    modalContent: css`
      background-color: white;
      border-radius: 8px;
      padding: 32px;
      max-width: 480px;
      width: 90%;
      max-height: 90vh;
      overflow-y: auto;
      outline: none;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      ${MEDIA_QUERY_FOR_SP} {
        width: 95%;
        padding: 24px;
      }
    `,
    modalTitle: css`
      font-size: 1.2rem;
      font-weight: bold;
      margin-bottom: 24px;
      text-align: center;
      color: #333;
    `,
    modalCardWrapper: css`
      margin-bottom: 24px;
      pointer-events: none;
      button {
        display: none;
      }
    `,
    modalButtons: css`
      display: flex;
      gap: 12px;
      justify-content: center;
      ${MEDIA_QUERY_FOR_SP} {
        flex-direction: column;
      }
    `,
    cancelButton: css`
      min-width: 120px;
      padding: 12px 24px;
      font-weight: bold;
      ${MEDIA_QUERY_FOR_SP} {
        min-width: auto;
        width: 100%;
      }
    `,
    deleteButton: css`
      min-width: 120px;
      padding: 12px 24px;
      font-weight: bold;
      color: #fff !important;
      background-color: #000 !important;
      box-shadow: none !important;
      &:hover {
        background-color: #666;
      }
      ${MEDIA_QUERY_FOR_SP} {
        min-width: auto;
        width: 100%;
      }
    `,
  };
};

const cardStyles = () => {
  return {
    goodsItem: css`
      position: relative;
      cursor: pointer;
      transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;
      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 0px 5px rgba(0, 0, 0, 0.15);
      }
      h3 {
        min-height: 40px;
        margin-bottom: 10px;
        font-size: 14px;
        font-weight: bold;
        line-height: 1.4em;
        overflow: hidden;
        ${MEDIA_QUERY_FOR_SP} {
          font-size: 1rem;
          min-height: 36px;
        }
      }
    `,
    imageContainer: css`
      position: relative;
      &::before {
        display: block;
        content: "";
        width: 100%;
        padding-top: 100%;
        background-color: #e3e3e3;
      }
      img {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    `,
    goodsItemInfo: css`
      padding: 15px;
      ${MEDIA_QUERY_FOR_SP} {
        padding: 12px;
      }
    `,
    goodsItemPrice: css`
      font-size: 14px;
      color: #333;
      margin-bottom: 5px;
      display: flex;
      justify-content: space-between;
      ${MEDIA_QUERY_FOR_SP} {
        font-size: 14px;
      }
    `,
  };
};
